import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { SvcToastService } from '@solvace/component/svc-toast';
import { SvcModalBosService } from '../modals/svc-modal-bos/shared/services/svc-modal-bos.service';
import { SvcModalBosType } from '../modals/svc-modal-bos/shared/enums/svc-modal-bos.enum';
import { parseBosNotificationPath } from './notification-bos-navigation.utils';

const NEW_TAB_NOTIFICATION_PATHS: readonly string[] = ['/notification/kct/'];

@Injectable({
  providedIn: 'root',
})
export class NotificationIntegrationService {
  private readonly _renderer2: Renderer2;
  private get _iframeEl(): HTMLIFrameElement {
    return this._document.querySelector<HTMLIFrameElement>('#notification-modal-integration') as HTMLIFrameElement;
  }

  constructor(
    @Inject(DOCUMENT) private readonly _document: Document,
    private readonly _renderer: RendererFactory2,
    private readonly _toastService: SvcToastService,
    private readonly _modalBosService: SvcModalBosService
  ) {
    this._renderer2 = this._renderer.createRenderer(null, null);
  }

  public addIntegration(): void {
    if (!this._iframeEl) {
      const html: string = `<iframe id="notification-modal-integration" class="hidden"></iframe>`;
      const template = this._renderer2.createElement('template');
      template.innerHTML = html.trim();
      this._renderer2.appendChild(this._document.body, template.content.firstChild);

      const iframe = this._iframeEl;
      iframe.addEventListener('load', () => {
        // Iframe loaded
      });

      iframe.addEventListener('error', () => {
        // Iframe error
      });

      window['closeAngularNotification'] = (): void => {
        this.closeIframe();
      };
      window['angularNotificationShowToast'] = (data, config): ReturnType<SvcToastService['showByConfig']> => {
        return this._toastService.showByConfig(data, config);
      };
      window['angularNotificationCloseToast'] = (id): void => {
        this._toastService.close(id);
      };
      window['angularNotificationClearToasts'] = (): void => {
        this._toastService.clear();
      };
      window['angularNotificationUnauthorized'] = (): void => {
        this.closeIframe();
      };
    }
  }

  public openIframeByPath(urlPath: string): void {
    if (!this._iframeEl) {
      this.addIntegration();
    }

    urlPath = !urlPath.startsWith('http') && !urlPath.startsWith('/') ? `/${urlPath}` : urlPath;
    urlPath = !urlPath.startsWith('http') && !urlPath.startsWith('/notification') ? `/notification${urlPath}` : urlPath;

    const bosParams = parseBosNotificationPath(urlPath);
    if (bosParams) {
      this._modalBosService.openModalBos(SvcModalBosType.view, bosParams.id, bosParams.uniqueId);
      return;
    }

    const origin = location.hostname == 'localhost' ? 'https://devsp.solvacelabs.com' : location.origin;
    const url = urlPath.startsWith('/') ? `${origin}${urlPath}` : urlPath;

    if (NEW_TAB_NOTIFICATION_PATHS.some((path) => url.includes(path))) {
      window.open(url, '_blank', 'noopener');
      return;
    }

    try {
      this._iframeEl.src = url;
      this._iframeEl.classList.remove('hidden', 'fadeOut');
      this._iframeEl.classList.add('fadeIn');

      const loadTimeout = setTimeout(() => {
        try {
          const iframeDoc = this._iframeEl.contentDocument || this._iframeEl.contentWindow?.document;
          if (!iframeDoc || !iframeDoc.body || iframeDoc.body.children.length === 0) {
            // Timeout - iframe may not have loaded
          }
        } catch {
          // Cross-origin error expected
        }
      }, 10000);

      this._iframeEl.addEventListener('load', () => {
        clearTimeout(loadTimeout);
      }, { once: true });

    } catch {
      // Handle error silently
    }
  }

  public closeIframe(): void {
    this._iframeEl.classList.add('fadeOut');
    this._iframeEl.classList.remove('fadeIn');
    setTimeout(() => {
      this._iframeEl.classList.add('hidden');
    }, 300);
  }

}
